export type ElevationRange = {
  minElevation: number;
  maxElevation: number;
};

const NICE_INTERVAL_STEPS = [1, 2, 2.5, 5, 10];
const INDEX_CONTOUR_INTERVAL = 2;

export const isValidElevationRange = (
  elevationRange: ElevationRange | null | undefined
): elevationRange is ElevationRange => {
  return (
    elevationRange !== null &&
    elevationRange !== undefined &&
    Number.isFinite(elevationRange.minElevation) &&
    Number.isFinite(elevationRange.maxElevation) &&
    elevationRange.maxElevation > elevationRange.minElevation
  );
};

export const getContourInterval = (
  elevationRange: ElevationRange,
  targetContourCount = 12
): number => {
  const span = elevationRange.maxElevation - elevationRange.minElevation;
  if (span <= 0) {
    return 1;
  }

  const rawInterval = span / targetContourCount;
  const magnitude = 10 ** Math.floor(Math.log10(rawInterval));
  const interval =
    NICE_INTERVAL_STEPS.find((step) => step * magnitude >= rawInterval) ??
    NICE_INTERVAL_STEPS.at(-1) ??
    10;

  return Number.parseFloat((interval * magnitude).toPrecision(6));
};

export const getContourLevels = (
  elevationRange: ElevationRange,
  interval = getContourInterval(elevationRange)
): number[] => {
  if (!isValidElevationRange(elevationRange) || interval <= 0) {
    return [];
  }

  const start = Math.ceil(elevationRange.minElevation / interval) * interval;
  const end = Math.floor(elevationRange.maxElevation / interval) * interval;
  const levels: number[] = [];
  const epsilon = interval / 1000;

  for (let level = start; level <= end + epsilon; level += interval) {
    if (
      level > elevationRange.minElevation + epsilon &&
      level < elevationRange.maxElevation - epsilon
    ) {
      levels.push(Number.parseFloat(level.toPrecision(8)));
    }
  }

  return levels;
};

export const normalizeElevation = (elevation: number, elevationRange: ElevationRange): number => {
  const span = elevationRange.maxElevation - elevationRange.minElevation;
  if (span <= 0) {
    return 0;
  }

  return (elevation - elevationRange.minElevation) / span;
};

export const denormalizeElevation = (
  normalizedElevation: number,
  elevationRange: ElevationRange
): number => {
  return (
    elevationRange.minElevation +
    normalizedElevation * (elevationRange.maxElevation - elevationRange.minElevation)
  );
};

const snapElevationToInterval = (elevation: number, interval: number): number => {
  if (interval <= 0) {
    return elevation;
  }

  return Math.round(elevation / interval) * interval;
};

/**
 * Extracts the [0.25, 0.5, 0.75] quantiles from normalized raster samples,
 * sub-sampling large arrays so the sort cost stays bounded.
 *
 * Returns `null` when there is not enough valid data to derive meaningful
 * quantiles, in which case callers should fall back to the elevation range.
 */
const sampleQuartileNormalizedValues = (
  samples: Float32Array,
  alpha: Uint8Array | undefined,
  maxSampleSize = 50_000
): [number, number, number] | null => {
  const stride = Math.max(1, Math.ceil(samples.length / maxSampleSize));
  const collected: number[] = [];

  for (let index = 0; index < samples.length; index += stride) {
    const value = samples[index];
    const isOpaque = alpha ? alpha[index] > 0 : true;
    if (isOpaque && Number.isFinite(value) && value > 0) {
      collected.push(value);
    }
  }

  if (collected.length < 32) {
    return null;
  }

  collected.sort((a, b) => a - b);
  const pickAt = (quantile: number) => {
    const position = (collected.length - 1) * quantile;
    return collected[Math.round(position)];
  };

  return [pickAt(0.25), pickAt(0.5), pickAt(0.75)];
};

export type ElevationSampleSource = {
  values: Float32Array;
  alpha?: Uint8Array;
};

export const computeElevationColorCutoffs = (
  elevationRange: ElevationRange,
  sampleSource?: ElevationSampleSource
): { cutoffMin: number; mid: number; cutoffMax: number } => {
  if (!isValidElevationRange(elevationRange)) {
    return { cutoffMin: 0.25, mid: 0.5, cutoffMax: 0.75 };
  }

  const interval = getContourInterval(elevationRange);
  const span = elevationRange.maxElevation - elevationRange.minElevation;
  const sampleQuantiles = sampleSource
    ? sampleQuartileNormalizedValues(sampleSource.values, sampleSource.alpha)
    : null;
  const normalizedQuantiles = sampleQuantiles ?? [0.25, 0.5, 0.75];

  const cutoffs = normalizedQuantiles.map((quantile) => {
    const elevation = elevationRange.minElevation + span * quantile;
    const snappedElevation = snapElevationToInterval(elevation, interval);
    return Math.min(0.95, Math.max(0.05, normalizeElevation(snappedElevation, elevationRange)));
  });

  // Ensure cutoffs stay monotonically increasing after snapping to the same contour level.
  const epsilon = 0.001;
  const cutoffMin = cutoffs[0];
  const mid = Math.max(cutoffMin + epsilon, cutoffs[1]);
  const cutoffMax = Math.max(mid + epsilon, cutoffs[2]);

  return {
    cutoffMin: Number.parseFloat(cutoffMin.toFixed(4)),
    mid: Number.parseFloat(Math.min(0.95, mid).toFixed(4)),
    cutoffMax: Number.parseFloat(Math.min(0.95, cutoffMax).toFixed(4)),
  };
};

const getDecimalsForInterval = (interval: number): number => {
  if (interval >= 1) {
    return 0;
  }

  const intervalText = interval.toString();
  const decimalPart = intervalText.split('.')[1];
  return decimalPart?.length ?? 1;
};

export const formatElevationLabel = (elevation: number, interval: number): string => {
  return `${elevation.toFixed(getDecimalsForInterval(interval))} m`;
};

export const isIndexContourLevel = (elevation: number, interval: number): boolean => {
  if (interval <= 0) {
    return false;
  }

  return (
    Math.abs(elevation / INDEX_CONTOUR_INTERVAL - Math.round(elevation / INDEX_CONTOUR_INTERVAL)) <
    0.001
  );
};
